import React, { FC, Fragment } from "react";
import { Platform, Text, type ViewProps } from "react-native";
import * as DialogPrimitive from "@rn-primitives/dialog";
import { XIcon } from "lucide-react-native";
import { FadeIn, FadeOut } from "react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "react-native-screens";

import { usePlatformOS } from "@/hooks/usePlatformOS";
import { ChildrenOrI18nType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox, VnThemedBox } from "./VnBox";
import { buttonVariants } from "./VnButton/variants";
import { VnIcon } from "./VnIcon";
import { VnNativeOnlyAnimatedView } from "./VnNativeOnlyAnimatedView";
import { VnText } from "./VnText/VnText";

export const VnDialog = DialogPrimitive.Root;

export const VnDialogTrigger = DialogPrimitive.Trigger;

export const VnDialogPortal = DialogPrimitive.Portal;

export const VnDialogClose = DialogPrimitive.Close;

const FullWindowOverlay =
  Platform.OS === "ios" ? RNFullWindowOverlay : Fragment;

type VnDialogOverlayPropsType = Omit<
  DialogPrimitive.OverlayProps,
  "asChild"
> & {
  children?: React.ReactNode;
};

export const VnDialogOverlay: FC<VnDialogOverlayPropsType> = ({
  className,
  children,
  ...props
}) => {
  const { isWeb, isNative } = usePlatformOS();

  return (
    <FullWindowOverlay>
      <DialogPrimitive.Overlay
        className={cn(
          "absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-black/50 p-2",
          isWeb &&
            "animate-in fade-in-0 fixed cursor-default [&>*]:cursor-auto",
          isNative && "px-6",
          className,
        )}
        {...props}
        asChild={!isWeb}>
        <VnThemedBox>
          <VnNativeOnlyAnimatedView
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(150)}>
            <VnNativeOnlyAnimatedView
              entering={FadeIn.delay(50)}
              exiting={FadeOut.duration(150)}>
              <>{children}</>
            </VnNativeOnlyAnimatedView>
          </VnNativeOnlyAnimatedView>
        </VnThemedBox>
      </DialogPrimitive.Overlay>
    </FullWindowOverlay>
  );
};

type VnDialogContentPropsType = DialogPrimitive.ContentProps & {
  portalHost?: string;
};

export const VnDialogContent: FC<VnDialogContentPropsType> = ({
  className,
  portalHost,
  children,
  ...props
}) => {
  const { isWeb } = usePlatformOS();

  return (
    <VnDialogPortal hostName={portalHost}>
      <VnDialogOverlay>
        <DialogPrimitive.Content
          className={cn(
            "border-border bg-card z-50 mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-4 rounded-lg border p-6 shadow-lg shadow-black/5 sm:max-w-lg",
            isWeb && "animate-in fade-in-0 zoom-in-95 duration-200",
            className,
          )}
          {...props}>
          {children}
        </DialogPrimitive.Content>
      </VnDialogOverlay>
    </VnDialogPortal>
  );
};

export const VnDialogHeader: FC<ViewProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <VnBox
      className={cn(
        "flex flex-row items-center justify-between gap-2 text-center sm:text-left",
        className,
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className={cn(buttonVariants({ variant: "outline" }))}
        hitSlop={12}>
        <VnIcon
          as={XIcon}
          className={cn(
            "text-accent-foreground web:pointer-events-none size-4 shrink-0",
          )}
        />
        <Text className="sr-only">Close</Text>
      </DialogPrimitive.Close>
    </VnBox>
  );
};

export const VnDialogFooter: FC<ViewProps> = ({ className, ...props }) => {
  return (
    <VnBox
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
};

type VnDialogTitlePropsType = DialogPrimitive.TitleProps &
  ChildrenOrI18nType & {
    primitiveTitleClassName?: string;
  };

export const VnDialogTitle: FC<VnDialogTitlePropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  primitiveTitleClassName,
  ...props
}) => {
  return (
    <DialogPrimitive.Title className={primitiveTitleClassName} {...props}>
      {children ?? (
        <VnText
          variant="headerTwo"
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className={cn("whitespace-nowrap leading-none", className)}
        />
      )}
    </DialogPrimitive.Title>
  );
};

type VnDialogDescriptionPropsType = DialogPrimitive.DescriptionProps &
  ChildrenOrI18nType;

export const VnDialogDescription: FC<VnDialogDescriptionPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  return (
    <DialogPrimitive.Description {...props}>
      {children ?? (
        <VnText
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className={cn("text-muted-foreground", className)}
        />
      )}
    </DialogPrimitive.Description>
  );
};
